import type React from 'react'
import {useState, useEffect, useCallback} from 'react'
import {useDispatch} from 'react-redux'
import {Button} from 'pepsico-ds'
import LoadingSpinner from '@/components/GDS/atoms/LoadingSpinner'
import type {BenchmarkMetadata} from '@/services/program/programService'
import {createGlobalBenchmark, getGlobalBenchmarks, patchGlobalBenchmark} from '@/services/program/programService'
import {clearCacheForEndpoint} from '@/services/interceptors/cacheInterceptor'
import {addToast} from '@/store/slices/toastSlice'
import {GLOBAL_KPI_LEFT_DISPLAY, GLOBAL_KPI_RIGHT_DISPLAY, TOAST_MESSAGES} from '../constants'
import UpdateKPIsSidePanel from './UpdateKPIsSidePanel'
import './globalSEMetricsTypography.css'

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Missing KPIs render a dash; present values append their unit (if any).
const formatValue = (value: number | null | undefined, unit = ''): string => (value == null ? '-' : `${value}${unit}`)

// ─── Component ────────────────────────────────────────────────────────────────

const ManageGlobalSEMetricsKPI: React.FC = () => {
  const dispatch = useDispatch()
  const [benchmark, setBenchmark] = useState<BenchmarkMetadata | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showPanel, setShowPanel] = useState(false)

  // ─── Load current benchmark ────────────────────────────────────────────────

  const loadBenchmark = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await getGlobalBenchmarks()
      setBenchmark(data.length > 0 ? data[0] : null)
    } catch {
      // Non-blocking; show empty state
      setBenchmark(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadBenchmark()
  }, [loadBenchmark])

  // ─── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async (data: BenchmarkMetadata) => {
    try {
      if (benchmark?.id) {
        await patchGlobalBenchmark(benchmark.id, data)
      } else {
        const created = await createGlobalBenchmark(data)
        setBenchmark(created)
      }
      setShowPanel(false)
      clearCacheForEndpoint('/benchmark')
      await loadBenchmark()
      dispatch(addToast({type: 'success', message: TOAST_MESSAGES.GLOBAL_KPI_UPDATE_SUCCESS}))
    } catch {
      dispatch(addToast({type: 'error', message: TOAST_MESSAGES.GLOBAL_KPI_UPDATE_ERROR}))
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="relative flex w-full flex-col gap-4 p-7">
      {/* Page header */}
      <div>
        <h2 className="text-section-header mb-1 h-[24px] w-[624px]">Global SE Metrics KPI</h2>
        <p className="text-paragraph-default">The KPIs mentioned below for the Global benchmark.</p>
      </div>

      {/* KPI display grid — rendered row-by-row (left/right paired by index) so the two
          columns stay aligned regardless of the two display lists' lengths. */}
      {isLoading ? (
        <LoadingSpinner centered />
      ) : (
        <div className="mt-2 grid grid-cols-2 gap-x-1 gap-y-6">
          {Array.from({length: Math.max(GLOBAL_KPI_LEFT_DISPLAY.length, GLOBAL_KPI_RIGHT_DISPLAY.length)}).flatMap(
            (_, rowIndex) =>
              [GLOBAL_KPI_LEFT_DISPLAY[rowIndex], GLOBAL_KPI_RIGHT_DISPLAY[rowIndex]].map(
                field =>
                  field && (
                    <div key={String(field.key)}>
                      <div className="text-paragraph-default">{field.label}</div>
                      <div className="mt-0.5 text-[14px] leading-[20px] font-bold text-[var(--color-text-primary)]">
                        {formatValue(benchmark?.[field.key], field.unit)}
                      </div>
                    </div>
                  ),
              ),
          )}
        </div>
      )}

      {/* Update button */}
      <div className="mt-4">
        <Button size="small" variant="primary" text="Update KPIs" onClick={() => setShowPanel(true)} />
      </div>

      {/* Side panel */}
      <UpdateKPIsSidePanel
        isOpen={showPanel}
        initialData={benchmark}
        onClose={() => setShowPanel(false)}
        onSave={handleSave}
      />
    </div>
  )
}

export default ManageGlobalSEMetricsKPI
